/**
 * The live half of the cursors: one channel per chain.
 *
 * PRESENCE SAYS WHO IS HERE, BROADCAST SAYS WHERE THEY ARE. They are two
 * mechanisms because they answer two questions at two rates. Presence
 * knows when somebody's tab closes — a broadcast channel never would,
 * and their cursor would sit on a box until it went stale. Broadcast
 * carries twenty positions a second without asking the server to keep
 * any of them, which is what presence would do with each one.
 *
 * The channel is PRIVATE. A Realtime channel is open to anybody who
 * knows its name otherwise, and the name is built from the anchor — so
 * without this flag, somebody signed in who guessed a project id would
 * learn who is working that tract and where they are looking. The policy
 * behind it is in schema-39 and calls lb_can_see_diagram, the same
 * function the diagram's own policies call.
 *
 * Nothing here is written down. A pointer position is worth nothing a
 * second after it arrives.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  chartTopic, cursorName, drawable, shouldSend, whoElse, withCursor, withMembers,
} from '../../lib/canvas/presence';

export default function useChartPresence({ place, profile, enabled = true }) {
  const topic = chartTopic(place || {});
  const me = profile?.id || null;
  const name = cursorName(profile);

  /**
   * WHAT IS KNOWN lives in a ref; WHAT IS DRAWN lives in state.
   *
   * Deciding which cursors to draw needs the time, and reading a clock
   * while rendering makes a component that returns something different
   * every time it is asked the same question. So the clock is read where
   * it belongs — when a message arrives, and on a timer that retires
   * cursors nobody has heard from — and the result is published as
   * ordinary state.
   */
  const known = useRef(new Map());
  const [cursors, setCursors] = useState([]);
  const [who, setWho] = useState('');
  const channel = useRef(null);
  const lastSent = useRef(null);
  // Held in a ref as well: the send path reads it on every pointer move
  // and must not re-subscribe the channel to do so. Kept up to date in
  // an effect rather than during render — a ref written while rendering
  // is a value that changes without anything being told about it.
  const mine = useRef({ me, name });
  useEffect(() => { mine.current = { me, name }; }, [me, name]);

  useEffect(() => {
    if (!enabled || !topic || !me) return undefined;
    let live = true;

    const room = supabase.channel(topic, {
      config: { private: true, presence: { key: me } },
    });

    const publish = () => {
      const now = Date.now();
      setCursors(drawable(known.current, now));
      setWho(whoElse(known.current));
    };

    room.on('broadcast', { event: 'cursor' }, ({ payload }) => {
      // Never draw yourself. Two windows of one person on one chain is
      // a real case — the tab and the chart's own window — and a cursor
      // chasing your own pointer is unnerving.
      if (!live || !payload || payload.id === mine.current.me) return;
      known.current = withCursor(known.current, payload, Date.now());
      publish();
    });

    room.on('presence', { event: 'sync' }, () => {
      if (!live) return;
      // The NAME comes from here, not only from the cursor messages: it
      // is tracked when somebody joins, so a person who is in the chain
      // but has not moved their pointer yet is still named. Learning
      // names only from movement showed everybody as "Someone" until
      // they happened to wave.
      const here = Object.entries(room.presenceState())
        .filter(([id]) => id !== mine.current.me)
        .map(([id, metas]) => ({ id, name: metas?.[0]?.name }));
      known.current = withMembers(known.current, here, Date.now());
      publish();
    });

    // Retires a cursor whose laptop went to sleep mid-drag. Without it
    // a pointer sits on a box forever and somebody waits for it to move
    // before touching that deed.
    const sweep = setInterval(publish, 2000);

    room.subscribe((status) => {
      if (!live) return;
      if (status === 'SUBSCRIBED') room.track({ name: mine.current.name });
      // Anything else — a policy that refused, a socket that dropped —
      // leaves the chart working with nobody on it, which is the right
      // way for this to fail. Cursors are a courtesy; the chain is the
      // work.
    });

    channel.current = room;
    return () => {
      live = false;
      clearInterval(sweep);
      channel.current = null;
      lastSent.current = null;
      known.current = new Map();
      setCursors([]);
      setWho('');
      supabase.removeChannel(room);
    };
  }, [topic, me, enabled]);

  /**
   * Send where the pointer is, in WORLD coordinates.
   *
   * Never in pixels: everybody has their own pan and zoom, so a position
   * in screen space lands on a different box for every person looking at
   * it — worse than no cursor, because it is confidently wrong.
   */
  const report = useCallback((at) => {
    const room = channel.current;
    if (!room) return;
    const now = Date.now();
    if (at === null) {
      // The pointer left the canvas. Said once, rather than rate
      // limited, or the last thing anybody heard is where it used to be.
      if (lastSent.current === null) return;
      lastSent.current = null;
      room.send({ type: 'broadcast', event: 'cursor', payload: { id: mine.current.me, name: mine.current.name } });
      return;
    }
    if (!shouldSend(lastSent.current, at, now)) return;
    lastSent.current = { x: at.x, y: at.y, at: now };
    room.send({
      type: 'broadcast',
      event: 'cursor',
      payload: {
        id: mine.current.me, name: mine.current.name, x: at.x, y: at.y,
      },
    });
  }, []);

  return { cursors, who, report };
}
